import tinycolor from 'tinycolor2';
import { getBlurOrReflectionParams } from '@/utils/draw-svg-kit';
import { type ILinearGradient, type IVectorEffectReflection } from '@/version-types';

export const createSVGFeComponentTransfer = (r: number, g: number, b: number) => {
    const svgNS = 'http://www.w3.org/2000/svg';

    const feComponentTransfer = document.createElementNS(svgNS, 'feComponentTransfer');
    feComponentTransfer.setAttribute('color-interpolation-filters', 'sRGB');

    const feFuncR = document.createElementNS(svgNS, 'feFuncR');
    feFuncR.setAttribute('type', 'linear');
    feFuncR.setAttribute('slope', '0');
    feFuncR.setAttribute('intercept', (r / 255).toString());

    const feFuncG = document.createElementNS(svgNS, 'feFuncG');
    feFuncG.setAttribute('type', 'linear');
    feFuncG.setAttribute('slope', '0');
    feFuncG.setAttribute('intercept', (g / 255).toString());

    const feFuncB = document.createElementNS(svgNS, 'feFuncB');
    feFuncB.setAttribute('type', 'linear');
    feFuncB.setAttribute('slope', '0');
    feFuncB.setAttribute('intercept', (b / 255).toString());

    const feFuncA = document.createElementNS(svgNS, 'feFuncA');
    feFuncA.setAttribute('type', 'linear');
    feFuncA.setAttribute('slope', '1');
    feFuncA.setAttribute('intercept', '0');

    feComponentTransfer.appendChild(feFuncR);
    feComponentTransfer.appendChild(feFuncG);
    feComponentTransfer.appendChild(feFuncB);
    feComponentTransfer.appendChild(feFuncA);

    return feComponentTransfer;
};

export const createSVGGroup = (attrs: Record<string, string>) => {
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    Object.entries(attrs).forEach(([key, value]) => {
        g.setAttribute(key, value);
    });

    return g;
};

export const createSVGUse = (attrs: Record<string, string>) => {
    const useElement = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    Object.entries(attrs).forEach(([key, value]) => {
        useElement.setAttribute(key, value);
    });

    return useElement;
};

export const createElementsForReflection = (id: string, reflection: IVectorEffectReflection) => {
    const svgNS = 'http://www.w3.org/2000/svg';
    const fadeID = `fade_${id}`;

    // --- 创建线性渐变 ---
    const linearGradient = document.createElementNS(svgNS, 'linearGradient');
    linearGradient.setAttribute('id', fadeID);
    linearGradient.setAttribute('gradientUnits', 'objectBoundingBox');
    linearGradient.setAttribute('gradientTransform', 'scale(0.33)');
    linearGradient.setAttribute('x1', '0');
    linearGradient.setAttribute('y1', '0');
    linearGradient.setAttribute('x2', '0');
    linearGradient.setAttribute('y2', '1');

    const stop1 = document.createElementNS(svgNS, 'stop');
    stop1.setAttribute('offset', `${100 - reflection.size}%`);
    stop1.setAttribute('stop-color', 'rgb(0,0,0)');
    stop1.setAttribute('stop-opacity', '0');

    const stop2 = document.createElementNS(svgNS, 'stop');
    stop2.setAttribute('offset', '100%');
    stop2.setAttribute('stop-color', 'rgb(255,255,255)');
    stop2.setAttribute('stop-opacity', '0.8');

    linearGradient.appendChild(stop1);
    linearGradient.appendChild(stop2);

    // --- 创建 mask ---
    const mask = document.createElementNS(svgNS, 'mask');
    mask.setAttribute('id', `mask_${id}`);
    mask.setAttribute('x', '-1');
    mask.setAttribute('y', '0');
    mask.setAttribute('width', '3');
    mask.setAttribute('height', '3');
    mask.setAttribute('maskContentUnits', 'objectBoundingBox');

    // --- 创建一个 rect 用于遮罩 ---
    const rect = document.createElementNS(svgNS, 'rect');
    rect.setAttribute('x', '-1');
    rect.setAttribute('y', '0');
    rect.setAttribute('width', '3');
    rect.setAttribute('height', '3');
    rect.setAttribute('fill', `url(#${fadeID})`);

    mask.appendChild(rect);

    return {
        linearGradient,
        mask,
    };
};

export function createSVGBlurFilter(id: string, options: { useFor: 'reflection'; extent: number }): SVGFilterElement;
export function createSVGBlurFilter(
    id: string,
    options: {
        useFor: 'blur' | 'shadow';
        extent: number;
        color: string | ILinearGradient;
    },
): SVGFilterElement;
export function createSVGBlurFilter(
    id: string,
    options: {
        useFor: 'blur' | 'shadow' | 'reflection';
        extent: number;
        color?: string | ILinearGradient;
    },
): SVGFilterElement {
    const svgNS = 'http://www.w3.org/2000/svg';
    const filter = document.createElementNS(svgNS, 'filter');
    const feGaussianBlur = document.createElementNS(svgNS, 'feGaussianBlur');

    const { useFor, extent, color } = options;

    // 主图模糊采用倒影的参数
    const { XY, WH } = getBlurOrReflectionParams(extent, useFor === 'blur' ? 'reflection' : useFor);
    filter.setAttribute('id', id);
    filter.setAttribute('x', XY);
    filter.setAttribute('y', XY);
    filter.setAttribute('width', WH);
    filter.setAttribute('height', WH);

    feGaussianBlur.setAttribute('in', useFor === 'reflection' || useFor === 'blur' ? 'SourceGraphic' : 'SourceAlpha');
    feGaussianBlur.setAttribute('stdDeviation', `${extent}`);

    filter.appendChild(feGaussianBlur);

    if (useFor !== 'reflection' && color) {
        const normalizedColor = typeof color === 'string' ? color : color.colors[0].color;
        const { r, g, b } = tinycolor(normalizedColor).toRgb();

        const feComponentTransfer = createSVGFeComponentTransfer(r, g, b);
        filter.appendChild(feComponentTransfer);
    }

    return filter;
}

/**
 * 创建主图模糊滤镜
 * @param id - 滤镜 ID
 * @param options - 模糊滤镜参数
 *  `options.width` - 滤镜宽度
 *  `options.height` - 滤镜高度
 *  `options.x` - 滤镜 x 坐标
 *  `options.y` - 滤镜 y 坐标
 *  `options.extent` - 模糊程度
 *
 * @returns SVGFilterElement
 */
export const createBlurFilter = (
    id: string,
    options: { width: string; height: string; x: string; y: string; extent: number },
): SVGFilterElement => {
    const { width, height, x, y, extent } = options;

    const svgNS = 'http://www.w3.org/2000/svg';

    const filter = document.createElementNS(svgNS, 'filter');
    filter.setAttribute('id', id);
    filter.setAttribute('x', `${x}`);
    filter.setAttribute('y', `${y}`);
    filter.setAttribute('width', `${width}`);
    filter.setAttribute('height', `${height}`);
    filter.setAttribute('filterUnits', 'userSpaceOnUse');
    filter.setAttribute('color-interpolation-filters', 'sRGB');

    const feFlood = document.createElementNS(svgNS, 'feFlood');
    feFlood.setAttribute('flood-opacity', '0');
    feFlood.setAttribute('result', 'BackgroundImageFix');

    const feBlend = document.createElementNS(svgNS, 'feBlend');
    feBlend.setAttribute('mode', 'normal');
    feBlend.setAttribute('in', 'SourceGraphic');
    feBlend.setAttribute('in2', 'BackgroundImageFix');

    const feGaussianBlur = document.createElementNS(svgNS, 'feGaussianBlur');
    feGaussianBlur.setAttribute('stdDeviation', `${extent}`);

    filter.appendChild(feFlood);
    filter.appendChild(feBlend);
    filter.appendChild(feGaussianBlur);

    return filter;
};
